import React from "react";
import {
	Button,
	ClassNameProps,
	makeObjectFromArrayMap,
	Row,
	StyleProps,
	Tooltip,
	useLocalStorage,
	valuesTypeFilter,
} from "@hex-insights/core";
import { TimelineFilterInput, TimelineObjectType, TimelineViewerType } from "../../Utilities";
import { Icon } from "../Icon";

const personalFilter: TimelineFilterInput = {
	asViewerType: TimelineViewerType.Author,
	objectTypeIn: [
		TimelineObjectType.Announcement,
		TimelineObjectType.CalendarEvent,
		TimelineObjectType.Newsletter,
		TimelineObjectType.Post,
	],
};

const socialFilter: TimelineFilterInput = {
	objectTypeIn: [
		TimelineObjectType.Announcement,
		TimelineObjectType.CalendarEvent,
		TimelineObjectType.Newsletter,
		TimelineObjectType.Post,
	],
};

export type TimelineViewerFilterSetName = "personal" | "social";

const timelineViewerFilterSetNames: TimelineViewerFilterSetName[] = ["personal", "social"];

export type TimelineViewerFilterSetsState = {
	filterSetName: TimelineViewerFilterSetName;
	setFilterSetName: (action: React.SetStateAction<TimelineViewerFilterSetName>) => void;
	filter: TimelineFilterInput;
};

export function useTimelineViewerFilterSets(
	allowedObjectTypes?: TimelineObjectType[],
	storageKeySuffix = "",
): TimelineViewerFilterSetsState {
	const storageKey = "timeline.viewerFilterSet" + (storageKeySuffix ? "." + storageKeySuffix : "");

	const { getItem, setItem } = useLocalStorage();

	const [filterSetName, setFilterSetName] = React.useState<TimelineViewerFilterSetName>(() =>
		getItem(storageKey, valuesTypeFilter(timelineViewerFilterSetNames, "personal")),
	);
	React.useEffect(() => {
		setItem(storageKey, filterSetName);
	}, [setItem, storageKey, filterSetName]);

	const filter = React.useMemo(() => {
		const allow = toAllowObject(allowedObjectTypes);

		switch (filterSetName) {
			case "personal":
				return {
					...personalFilter,
					objectTypeIn: personalFilter.objectTypeIn?.filter((e) => allow.all || allow.allowed[e]),
				};
			case "social":
				return {
					...socialFilter,
					objectTypeIn: socialFilter.objectTypeIn?.filter((e) => allow.all || allow.allowed[e]),
				};
		}
	}, [filterSetName, allowedObjectTypes]);

	return { filterSetName, setFilterSetName, filter };
}

export type TimelineViewerFilterSetsFormProps = {
	filterSetName: TimelineViewerFilterSetName;
	setFilterSetName: (action: React.SetStateAction<TimelineViewerFilterSetName>) => void;
} & Partial<ClassNameProps & StyleProps>;

export function TimelineViewerFilterSetsForm({
	filterSetName,
	setFilterSetName,
	className,
	style,
}: TimelineViewerFilterSetsFormProps) {
	const onPersonalSocialClick = React.useCallback(() => {
		setFilterSetName("personal");
	}, [setFilterSetName]);

	const onAllSocialClick = React.useCallback(() => {
		setFilterSetName("social");
	}, [setFilterSetName]);

	return (
		<Row justify="spaced-start" align="center" className={className} style={style}>
			<Tooltip.Container side="bottom">
				<Tooltip noStem className="tooltip--inverted">
					<Tooltip.Body>Personal</Tooltip.Body>
				</Tooltip>

				<Button
					variant={filterSetName === "personal" ? "primary" : "tertiary"}
					onClick={onPersonalSocialClick}
					className="button--icon"
				>
					<Icon.User size={16} className="block" />
				</Button>
			</Tooltip.Container>

			<Tooltip.Container side="bottom">
				<Tooltip noStem className="tooltip--inverted">
					<Tooltip.Body>Social</Tooltip.Body>
				</Tooltip>

				<Button
					variant={filterSetName === "social" ? "primary" : "tertiary"}
					onClick={onAllSocialClick}
					className="button--icon"
				>
					<Icon.Users size={16} className="block" />
				</Button>
			</Tooltip.Container>
		</Row>
	);
}

function toAllowObject(
	allowedObjectTypes?: TimelineObjectType[],
): { all: true; allowed: null } | { all: false; allowed: Record<TimelineObjectType, boolean> } {
	if (!allowedObjectTypes) {
		return { all: true, allowed: null };
	}

	return {
		all: false,
		allowed: makeObjectFromArrayMap(allowedObjectTypes, (e) => [e, true]),
	};
}
